import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  TextField,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  Assignment, 
  ArrowForward,
  Refresh,
  Settings,
  Save
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';


// Color configuration (copied from DockManagement.tsx)
const colorConfig = {
  mdt: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  detention: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  taskAge: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 120, // 2 hours in minutes
    criticalThreshold: 240, // 4 hours in minutes
  },
  card: {
    default: '#ffffff',
    oneWarning: '#fef3c7',
    twoWarnings: '#fde68a',
    threeWarnings: '#fcd34d',
    oneCritical: '#fee2e2',
    twoCriticals: '#fecaca',
    threeCriticals: '#fca5a5',
    mixed: '#f3f4f6',
    textColor: '#1f2937'
  }
};

// Helper function to extract minutes from timer string
const getMinutesFromTimer = (timeString: string): number => {
  if (!timeString) return 0;
  
  const isNegative = timeString.startsWith('-');
  const cleanTimeString = isNegative ? timeString.substring(1) : timeString;
  
  const parts = cleanTimeString.split(':');
  if (parts.length >= 2) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parts.length > 2 ? (parseInt(parts[2], 10) || 0) / 60 : 0;
    
    return (hours * 60 + minutes + seconds) * (isNegative ? -1 : 1);
  }
  
  return 0;
};

// Helper function for timer calculations
const calculateCountdownTime = (targetDateString: string, timezone = 'America/New_York'): string => {
  try {
    const targetDate = new Date(targetDateString);
    const now = new Date();
    const diffMs = targetDate.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      const absDiffMs = Math.abs(diffMs);
      const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `-${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    } else {
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    }
  } catch (error) {
    return "00:00:00";
  }
};

// Helper function to get task age in minutes
const getTaskAgeMinutes = (createdAt: string): number => {
  try {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    return Math.floor(diffMs / (1000 * 60));
  } catch (error) {
    return 0;
  }
};

// Helper function to get time status
const getTimeStatus = (timeString: string, timerType: 'mdt' | 'detention' | 'taskAge'): 'red' | 'green' | 'orange' => {
  if (timerType === 'taskAge') {
    const minutes = getTaskAgeMinutes(timeString);
    const config = colorConfig[timerType];
    
    if (minutes >= config.criticalThreshold) return 'red';
    if (minutes >= config.warningThreshold) return 'orange';
    return 'green';
  }
  
  // For MDT and detention
  if (!timeString || timeString === '00:00:00') {
    return 'green';
  }
  
  const minutes = getMinutesFromTimer(timeString);
  const config = colorConfig[timerType];
  
  if (minutes > 0) {
    if (minutes <= config.criticalThreshold) return 'red';
    if (minutes <= config.warningThreshold) return 'orange';
    return 'green';
  } else {
    return 'red'; // Overdue
  }
};

// Types
interface Facility {
  id: number;
  name: string;
}

interface Task {
  uuid: string;
  data: any;
  due_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  task_type?: {
    type: string;
  };
  task_status?: {
    status: string;
  };
  trailer?: {
    trailer_number?: string;
    carrier?: {
      name: string;
    };
  };
  from_location?: string;
  to_location?: string;
  mdt?: string;
  detention?: string;
  groupedTasks?: Task[]; // For tracking grouped tasks
}

// Widget configuration interface
interface TaskWidgetConfig {
  showOnlyNeedingAttention: boolean;
  showOnlyOverdue: boolean;
  ageThresholdHours: number;
  showStatusCount: boolean;
  sortBy: 'created_at' | 'due_at' | 'type' | 'urgency';
  sortOrder: 'asc' | 'desc';
  statusFilter: 'all' | 'active' | 'pending' | 'assigned';
}

interface TasksDashboardWidgetProps {
  selectedFacility: Facility | null;
  compactMode: boolean;
  data?: Record<string, any> | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
}

// Helper function to determine if task needs attention
const needsAttention = (task: Task, hoursThreshold: number): boolean => {
  // Check timer urgency
  const mdtStatus = getTimeStatus(task.mdt || '00:00:00', 'mdt');
  const detentionStatus = getTimeStatus(task.detention || '00:00:00', 'detention');
  const ageStatus = getTimeStatus(task.created_at, 'taskAge');
  
  // If any timer is critical or orange, needs attention
  if (mdtStatus === 'red' || detentionStatus === 'red' || ageStatus === 'red' ||
      mdtStatus === 'orange' || detentionStatus === 'orange' || ageStatus === 'orange') {
    return true;
  }
  
  return false;
};

// Helper function to get task card background color based on urgency
const getTaskCardColor = (task: Task, hoursThreshold: number): string => {
  // Get timer statuses
  const mdtStatus = getTimeStatus(task.mdt || '00:00:00', 'mdt');
  const detentionStatus = getTimeStatus(task.detention || '00:00:00', 'detention');
  const ageStatus = getTimeStatus(task.created_at, 'taskAge');
  
  const statuses = [mdtStatus, detentionStatus, ageStatus];
  const warningCount = statuses.filter(status => status === 'orange').length;
  const criticalCount = statuses.filter(status => status === 'red').length;

  // Apply timer-based coloring
  if (criticalCount >= 3) return colorConfig.card.threeCriticals;
  if (criticalCount === 2) return colorConfig.card.twoCriticals;
  if (criticalCount === 1) return colorConfig.card.oneCritical;
  if (warningCount >= 3) return colorConfig.card.threeWarnings;
  if (warningCount === 2) return colorConfig.card.twoWarnings;
  if (warningCount === 1) return colorConfig.card.oneWarning;
  if (warningCount > 0 || criticalCount > 0) return colorConfig.card.mixed;
  
  return colorConfig.card.default;
};

// Helper function to get display task type
const getDisplayTaskType = (task: Task): string => {
  return task.task_type?.type?.replace(/_/g, ' ') || 'UNKNOWN';
};

// Helper function to group tasks by trailer UUID (copied from TaskApp.tsx)
const groupTasks = (tasks: Task[]): Task[] => {
  const trailerGroups: Record<string, Task[]> = {};
  const ungroupedTasks: Task[] = [];
  
  tasks.forEach(task => {
    const trailerUuid = task.data?.trailer_uuid;
    if (trailerUuid) {
      if (!trailerGroups[trailerUuid]) {
        trailerGroups[trailerUuid] = [];
      }
      trailerGroups[trailerUuid].push(task);
    } else {
      ungroupedTasks.push(task);
    }
  });
  
  // For grouped tasks, only show MOVE_TRAILER task (if it exists and active), otherwise show first task
  const displayTasks: Task[] = [];
  
  Object.values(trailerGroups).forEach(group => {
    if (group.length === 1) {
      displayTasks.push(group[0]);
    } else {
      // Multiple tasks, prefer MOVE_TRAILER if it's active
      const moveTrailerTask = group.find(task => 
        task.task_type?.type === 'MOVE_TRAILER' && 
        task.task_status?.status !== 'COMPLETED' && 
        task.task_status?.status !== 'CANCELLED'
      );
      const selectedTask = moveTrailerTask || group[0];
      // Add group info for display purposes
      selectedTask.groupedTasks = group;
      displayTasks.push(selectedTask);
    }
  });
  
  // Add ungrouped tasks
  displayTasks.push(...ungroupedTasks);
  
  return displayTasks;
};

const TasksDashboardWidget: React.FC<TasksDashboardWidgetProps> = ({
  selectedFacility,
  compactMode,
  data: dashboardData,
  isRefreshing = false,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  // Widget configuration with localStorage persistence
  const [config, setConfig] = useState<TaskWidgetConfig>(() => {
    const saved = localStorage.getItem('taskWidgetConfig');
    return saved ? JSON.parse(saved) : {
      showOnlyNeedingAttention: true,
      showOnlyOverdue: false,
      ageThresholdHours: 4,
      showStatusCount: true,
      sortBy: 'urgency',
      sortOrder: 'desc',
      statusFilter: 'all'
    };
  });

  // Save config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('taskWidgetConfig', JSON.stringify(config));
  }, [config]);

  // Process raw data from consolidated query
  const tasks: Task[] = React.useMemo(() => {
    const allTasks = dashboardData?.tasksByFacility?.tasks || [];
    const activeTasks = allTasks.filter((task: Task) =>
      !task.completed_at &&
      task.task_status?.status !== 'COMPLETED' &&
      task.task_status?.status !== 'CANCELLED'
    );
    return activeTasks.map((task: any) => ({
      ...task,
      mdt: "",
      detention: ""
    }));
  }, [dashboardData?.tasksByFacility]);

  const loading = !dashboardData && !error;

  // Navigate to specific task
  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  // Navigate to full tasks page
  const handleViewAll = () => {
    navigate('/taskapp');
  };

  // Save widget configuration
  const handleSaveConfig = async () => {
    if (!selectedFacility) return;

    try {
      setSaving(true);
      console.log('Task widget configuration saved successfully');
    } catch (err) {
      console.error('Failed to save widget configuration:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Group and filter tasks
  const groupedTasks = groupTasks(tasks);
  
  // Apply filters
  let filteredTasks = [...groupedTasks];
  
  // Apply status filter
  if (config.statusFilter !== 'all') {
    filteredTasks = filteredTasks.filter(task => 
      task.task_status?.status?.toLowerCase() === config.statusFilter
    );
  }
  
  // Apply overdue filter
  if (config.showOnlyOverdue) {
    filteredTasks = filteredTasks.filter(task => {
      const taskAgeHours = getTaskAgeMinutes(task.created_at) / 60;
      return taskAgeHours >= config.ageThresholdHours;
    });
  }
  
  // Apply attention filter
  if (config.showOnlyNeedingAttention) {
    filteredTasks = filteredTasks.filter(task => 
      needsAttention(task, config.ageThresholdHours)
    );
  }

  // Apply sorting
  filteredTasks.sort((a, b) => {
    let aValue: any, bValue: any;
    
    switch (config.sortBy) {
      case 'created_at':
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      case 'due_at':
        aValue = a.due_at ? new Date(a.due_at).getTime() : 0;
        bValue = b.due_at ? new Date(b.due_at).getTime() : 0;
        break;
      case 'type':
        aValue = getDisplayTaskType(a);
        bValue = getDisplayTaskType(b);
        break;
      case 'urgency':
        // Sort by urgency (critical timers first)
        const aUrgent = needsAttention(a, config.ageThresholdHours);
        const bUrgent = needsAttention(b, config.ageThresholdHours);
        if (aUrgent && !bUrgent) return -1;
        if (!aUrgent && bUrgent) return 1;
        // If both urgent or both not urgent, sort by age
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      default:
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return config.sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return config.sortOrder === 'asc' ? (aValue - bValue) : (bValue - aValue);
    }
  });

  // Get status counts
  const statusCounts = {
    all: tasks.length,
    needingAttention: tasks.filter(t => needsAttention(t, config.ageThresholdHours)).length,
    overdue: tasks.filter(t => {
      const taskAgeHours = getTaskAgeMinutes(t.created_at) / 60;
      return taskAgeHours >= config.ageThresholdHours;
    }).length,
    active: tasks.filter(t => t.task_status?.status === 'ACTIVE').length,
    pending: tasks.filter(t => t.task_status?.status === 'PENDING').length
  };

  const displayTasks = compactMode ? filteredTasks.slice(0, 8) : filteredTasks.slice(0, 12);

  return (
    <Paper sx={{ 
      p: 3, 
      height: showConfig ? 'auto' : (compactMode ? 320 : 400),
      minHeight: compactMode ? 320 : 400,
      display: 'flex',
      flexDirection: 'column',
      overflow: showConfig ? 'visible' : 'hidden'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Assignment sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            {config.showOnlyNeedingAttention ? 'Tasks Needing Attention' : 'Active Tasks'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Save Configuration">
            <IconButton 
              onClick={handleSaveConfig} 
              disabled={saving || !selectedFacility}
              size="small"
              color="primary"
            >
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configure">
            <IconButton 
              onClick={() => setShowConfig(!showConfig)} 
              size="small"
              color={showConfig ? 'primary' : 'default'}
            >
              <Settings />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading && !isRefreshing} size="small">
              <Refresh className={isRefreshing ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Configuration Panel */}
      <Collapse in={showConfig}>
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Configuration</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showOnlyNeedingAttention}
                onChange={(e) => setConfig(prev => ({ ...prev, showOnlyNeedingAttention: e.target.checked }))}
                size="small"
              />
            }
            label="Show only tasks needing attention"
            sx={{ display: 'block', mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showOnlyOverdue}
                onChange={(e) => setConfig(prev => ({ ...prev, showOnlyOverdue: e.target.checked }))}
                size="small"
              />
            }
            label={`Show only tasks older than ${config.ageThresholdHours}h`}
            sx={{ display: 'block', mb: 1 }}
          />
          
          <TextField
            label="Age threshold (hours)"
            type="number"
            size="small"
            value={config.ageThresholdHours}
            onChange={(e) => setConfig(prev => ({ ...prev, ageThresholdHours: parseInt(e.target.value) || 4 }))}
            sx={{ width: 150, mb: 1 }}
            inputProps={{ min: 1, max: 24 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showStatusCount}
                onChange={(e) => setConfig(prev => ({ ...prev, showStatusCount: e.target.checked }))}
                size="small"
              />
            }
            label="Show status counts"
            sx={{ display: 'block', mb: 2 }}
          />

          {/* Sort and Filter Controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={config.sortBy}
                label="Sort By"
                onChange={(e) => setConfig(prev => ({ ...prev, sortBy: e.target.value as any }))}
              >
                <MenuItem value="urgency">Urgency</MenuItem>
                <MenuItem value="created_at">Created Time</MenuItem>
                <MenuItem value="due_at">Due Date</MenuItem>
                <MenuItem value="type">Task Type</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={config.sortOrder}
                label="Order"
                onChange={(e) => setConfig(prev => ({ ...prev, sortOrder: e.target.value as any }))}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={config.statusFilter}
                label="Status Filter"
                onChange={(e) => setConfig(prev => ({ ...prev, statusFilter: e.target.value as any }))}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>

      {!selectedFacility ? (
        <Typography variant="body2" color="warning.main">
          Select a facility to view tasks
        </Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active tasks
        </Typography>
      ) : (
        <>
          {/* Status Overview */}
          {config.showStatusCount && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {config.showOnlyNeedingAttention && (
                <Chip 
                  label={`${statusCounts.needingAttention} Need Attention`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip 
                label={`${statusCounts.overdue} Older than ${config.ageThresholdHours}h`}
                color="warning"
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`${statusCounts.active} Active`}
                color="info"
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`${statusCounts.pending} Pending`}
                color="warning"
                size="small"
                variant="outlined"
              />
            </Box>
          )}

          {filteredTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {config.showOnlyNeedingAttention 
                ? `No tasks need attention (${config.ageThresholdHours}h threshold)`
                : config.showOnlyOverdue 
                  ? `No tasks older than ${config.ageThresholdHours} hours`
                  : "No tasks match the current filters"
              }
            </Typography>
          ) : (
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}>
              {/* Grid of Task Cards */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: compactMode ? 'repeat(auto-fill, 80px)' : 'repeat(auto-fill, 100px)', 
                gap: 0.5, 
                flexGrow: 1, 
                overflow: 'auto',
                maxHeight: compactMode ? '240px' : '300px'
              }}>
                {displayTasks.map((task) => (
                  <Tooltip 
                    key={task.uuid}
                    title={`${getDisplayTaskType(task)} - ${task.task_status?.status || 'PENDING'} ${task.groupedTasks ? `(${task.groupedTasks.length} tasks grouped)` : ''}`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        aspectRatio: '1',
                        cursor: 'pointer',
                        border: '2px solid #9CA3AF',
                        backgroundColor: getTaskCardColor(task, config.ageThresholdHours),
                        color: '#000000',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        transition: 'opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleTaskClick(task.uuid)}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: compactMode ? '10px' : '11px',
                          textAlign: 'center'
                        }}
                      >
                        {getDisplayTaskType(task)}
                      </Typography>
                      
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: compactMode ? '9px' : '10px',
                          mt: 0.3,
                          textAlign: 'center',
                          px: 0.5,
                          lineHeight: 1.2,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {task.task_status?.status || 'PENDING'}
                      </Typography>
                      
                      {/* Show trailer number if available */}
                      {task.trailer?.trailer_number && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: compactMode ? '7px' : '8px',
                            textAlign: 'center',
                            px: 0.5,
                            lineHeight: 1,
                            color: 'rgba(0,0,0,0.7)',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {task.trailer.trailer_number}
                        </Typography>
                      )}

                      {/* Show grouped task count */}
                      {task.groupedTasks && task.groupedTasks.length > 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            borderRadius: '50%',
                            width: compactMode ? '14px' : '16px',
                            height: compactMode ? '14px' : '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: compactMode ? '8px' : '9px',
                            fontWeight: 'bold'
                          }}
                        >
                          {task.groupedTasks.length}
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {filteredTasks.length > displayTasks.length && (
              <Typography variant="caption" color="text.secondary">
                +{filteredTasks.length - displayTasks.length} more {config.showOnlyOverdue ? `older than ${config.ageThresholdHours}h` : config.showOnlyNeedingAttention ? 'needing attention' : 'tasks'}
              </Typography>
            )}
            <Button 
              size="small" 
              endIcon={<ArrowForward />}
              onClick={handleViewAll}
              sx={{ ml: 'auto' }}
            >
              Manage All
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default TasksDashboardWidget; 